import { useRouteLoaderData } from '@remix-run/react';

import useIsLoggedIn from '~/hooks/useIsLoggedIn';
import type { loader as communityLoader } from '~/routes/community';

import { classifyJoinGate, type JoinGateStatus } from '../_utils/classifyJoinGate';
import { isConditionLock } from '../_utils/communityAccess';
import { useCommunityAccess } from './useCommunityAccess';
import { useCommunityMembership } from './useCommunityMembership';
import { useCommunityPurchasableTiers } from './useCommunityPurchasableTiers';

/**
 * Classifies the community_join intent for the current viewer. `enabled` gates the
 * pricing fetch; by default it only runs while the viewer lacks access (with access
 * the gate is joinable regardless of the catalog).
 */
export const useCommunityJoinGate = ({ enabled }: { enabled?: boolean } = {}): JoinGateStatus => {
  const layoutData = useRouteLoaderData<typeof communityLoader>('routes/community');
  const { isCommunityAccessible } = useCommunityAccess();
  const { isMember } = useCommunityMembership();
  // The root's client loader sets isLoggedIn after the first render, and a member is always signed in.
  const isLoggedIn = useIsLoggedIn() || isMember;
  const lockReason = layoutData?.community?.lock_reason ?? null;
  // A condition lock is decided before the catalog, so the pricing isn't fetched for it.
  const { hasPurchasableTier } = useCommunityPurchasableTiers({
    enabled: (enabled ?? !isCommunityAccessible) && !isConditionLock(lockReason),
  });

  return classifyJoinGate({
    gated: Boolean(layoutData?.community?.gated),
    accessible: isCommunityAccessible,
    hasPurchasableTier,
    lockReason,
    isLoggedIn,
  });
};
